import { LIST_DASHBOARDS_NAME } from "@/lib/ai/tools/list-dashboards/constants";
import type { ListDashboardsOutput } from "@/lib/ai/tools/list-dashboards/server";
import {
  defineDatasetSpec,
  expectOptionalArrayField,
  expectVisualizationObjectOutput,
} from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListNumber,
  toListText,
  toListTextList,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const DASHBOARDS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_DASHBOARDS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.dashboards" }),
  fields: [
    { key: "organizationId", role: "dim", type: "string" },
    { key: "dashboardId", role: "dim", type: "string" },
    { key: "dashboardName", role: "dim", type: "string" },
    { key: "description", role: "dim", type: "string" },
    { key: "sortOrder", role: "metric", type: "number", unit: "count" },
    { key: "itemCount", role: "metric", type: "number", unit: "count" },
    { key: "metricItemCount", role: "metric", type: "number", unit: "count" },
    { key: "itemNamesPreview", role: "dim", type: "string" },
    { key: "itemTypesPreview", role: "dim", type: "string" },
    { key: "createdAt", role: "dim", type: "string" },
    { key: "updatedAt", role: "dim", type: "string" },
  ] as const,
  defaults: { category: "dashboardName", value: "itemCount" },
  notes: "Table dataset normalized from list_dashboards output.",
});

function toDashboardRows(
  output: Partial<ListDashboardsOutput> & Record<string, unknown>,
): VizDatasetRow[] {
  const dashboards =
    expectOptionalArrayField<
      NonNullable<ListDashboardsOutput["dashboards"]>[number]
    >(output, "dashboards", LIST_DASHBOARDS_NAME) ?? [];
  const order =
    expectOptionalArrayField<string>(
      output,
      "dashboardOrder",
      LIST_DASHBOARDS_NAME,
    ) ?? [];
  const orderMap = new Map(
    order.map((dashboardId, index) => [dashboardId, index + 1]),
  );

  return dashboards.map((dashboard) => {
    const items = Array.isArray(dashboard.items) ? dashboard.items : [];
    const metricItems = items.filter((item) => item.type !== "DIVIDER");
    const itemNames = items.map((item) => item.name);
    const itemTypes = items.map((item) => item.type);

    return {
      organizationId: toListText(output.organizationId),
      dashboardId: toListText(dashboard.id),
      dashboardName: toListText(dashboard.name),
      description: toListText(dashboard.description),
      sortOrder: toListNumber(orderMap.get(dashboard.id)),
      itemCount: toListNumber(items.length),
      metricItemCount: toListNumber(metricItems.length),
      itemNamesPreview: toListTextList(itemNames),
      itemTypesPreview: toListTextList(itemTypes),
      createdAt: toListText(dashboard.createdAt),
      updatedAt: toListText(dashboard.updatedAt),
    };
  });
}

/**
 * list_dashboards 出力を table-only dataset へ正規化する。
 */
export function buildListDashboardsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput = expectVisualizationObjectOutput<
    Partial<ListDashboardsOutput>
  >({
    toolName: LIST_DASHBOARDS_NAME,
    output,
  });
  const rows = toDashboardRows(typedOutput);
  const dataset = buildListTableDataset({
    id: DASHBOARDS_DATASET_SPEC.id,
    datasetFamily: DASHBOARDS_DATASET_SPEC.profile.datasetFamily,
    variant: DASHBOARDS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: DASHBOARDS_DATASET_SPEC.fields,
    defaults: DASHBOARDS_DATASET_SPEC.defaults,
    notes: DASHBOARDS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
